import { AvatarUpload } from "@features/profile/components/avatar-upload";
import ProfileHeader from "@features/profile/components/profile-header";
import { useUpdateProfile } from "@features/profile/hooks/use-profile.hook";
import { useProfileStore } from "@features/profile/profile.store";
import { useAppForm } from "@shared/lib/form";
import { Button } from "@shared/ui/button";
import { Card, CardContent, CardHeader } from "@shared/ui/card";
import { Loader2 } from "lucide-react";
import { useIntl } from "react-intl";
import { toast } from "sonner";

export function GeneralPage() {
	const intl = useIntl();

	const profile = useProfileStore((state) => state.profile);
	const updateProfile = useUpdateProfile();

	const updateSuccess = intl.formatMessage({
		id: "api.profile.update.success",
		defaultMessage: "Profile updated",
	});

	const updateError = intl.formatMessage({
		id: "api.profile.update.error",
		defaultMessage: "Error when updating:",
	});

	const form = useAppForm({
		defaultValues: {
			first_name: profile?.first_name || "",
			last_name: profile?.last_name || "",
			email: profile?.email || "",
		},
		onSubmit: ({ value }) => {
			updateProfile.mutate(value, {
				onSuccess: () => {
					toast.success(updateSuccess);
				},
				onError: (error) => {
					toast.error(`${updateError} ${error}`);
				},
			});
		},
	});

	return (
		<div className="space-y-6">
			<ProfileHeader
				title={intl.formatMessage({
					id: "page.profile.profile.title",
					defaultMessage: "Profile",
				})}
				description={intl.formatMessage({
					id: "page.profile.profile.subTitle",
					defaultMessage: "Personal information and contact details",
				})}
			/>
			<Card>
				<CardHeader>
					<AvatarUpload
						avatarUrl={profile?.avatar_url ?? null}
						firstName={profile?.first_name ?? ""}
						lastName={profile?.last_name ?? ""}
					/>
					<div className="border-t border-border" />
				</CardHeader>
				<CardContent>
					<form
						onSubmit={(e) => {
							e.preventDefault();
							form.handleSubmit();
						}}
						className="space-y-6"
					>
						<div className="grid gap-5 sm:grid-cols-2">
							<form.AppField name="first_name">
								{(field) => (
									<field.TextField
										label={intl.formatMessage({
											id: "page.profile.general.firstName",
											defaultMessage: "First Name",
										})}
									/>
								)}
							</form.AppField>
							<form.AppField name="last_name">
								{(field) => (
									<field.TextField
										label={intl.formatMessage({
											id: "page.profile.general.lastName",
											defaultMessage: "Last Name",
										})}
									/>
								)}
							</form.AppField>
							<form.AppField name="email">
								{(field) => (
									<field.TextField
										label={intl.formatMessage({
											id: "page.profile.general.email",
											defaultMessage: "Email",
										})}
										readOnly
										disabled
									/>
								)}
							</form.AppField>
						</div>

						<div className="flex justify-end gap-2 pt-2">
							<Button type="submit" disabled={updateProfile.isPending} className="min-w-32">
								{updateProfile.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
								{intl.formatMessage({
									id: "page.profile.general.save",
									defaultMessage: "Save",
								})}
							</Button>
						</div>
					</form>
				</CardContent>
			</Card>
		</div>
	);
}
